// Per-project placement overrides for the streaming viewer.
//
// The project card's placement dialog lets the owner move, turn, scale
// or hide each Revit file of the model. The entries are saved on the
// project (GET /projects/:id/document-transforms); this module fetches
// them once per project when a manifest is loaded and answers, while
// sidecars hydrate, "what matrix does this row's document add?" and
// "is this row's document hidden?". Rows are attributed to a document
// by the exporter's tag `f` (index into manifest.documents) or `doc`.
//
// Applied at ingest (TileRegistry.hydrate), so everything downstream -
// pools, batches, picking, distance, tile boxes - sees the moved
// geometry as if the export had placed it there.
import { Box3, Matrix4, Vector3 } from 'three';
import { getDocumentTransforms, getProjectModelDocuments } from 'api/projects/modelSources';
import { buildOverrideMatrix, isIdentityEntry, matchDocumentIndex } from './documentTransformMath';

const cache = new Map();          // projectId -> { entries, fetchedAt }
let active = null;                // { projectId, byIndex: Map<idx, Matrix4>, hiddenIdx: Set, hiddenIds: Set, byId: Map<id, Matrix4>, matrices: Matrix4[] }

async function fetchEntries(projectId) {
  const hit = cache.get(projectId);
  if (hit && Date.now() - hit.fetchedAt < 15000) return hit.entries;
  let entries = [];
  try {
    const r = await getDocumentTransforms(projectId);
    entries = Array.isArray(r?.entries) ? r.entries : [];
  } catch (e) {
    entries = [];              // no access / offline: the export as published
  }
  cache.set(projectId, { entries, fetchedAt: Date.now() });
  return entries;
}

// The model's centre in scene coordinates, from the manifest's tile
// boxes - the manifest carries no global bounds of its own.
export function pivotFromManifest(manifest) {
  const box = new Box3();
  for (const t of (manifest && manifest.tiles) || []) {
    if (!t || !t.bbox || t.outlier === true) continue;
    box.expandByPoint(new Vector3(...t.bbox.min));
    box.expandByPoint(new Vector3(...t.bbox.max));
  }
  if (box.isEmpty()) return new Vector3();
  return box.getCenter(new Vector3());
}

// Build the active state from entries. Exported for tests and for the
// dialog's preview; activate() is what the loader calls.
//
// Rows are attributed to a file two ways, first wins: the exporter's
// tag (`f` / `doc`), on exports with documentsTagged; and the 36-char
// episode prefix of the row's UniqueId, which the server derives from
// the record trees for every export ever published (`serverDocuments`
// is GET /projects/:id/model-documents, whose documents[] carry
// `prefixes`). Without the second, an override on an older export
// would silently do nothing.
export function activateWith(projectId, manifest, entries, serverDocuments) {
  const documents = Array.isArray(manifest?.documents) ? manifest.documents : [];
  const known = Array.isArray(serverDocuments) ? serverDocuments : [];
  const pivot = pivotFromManifest(manifest);
  const byIndex = new Map();
  const byId = new Map();
  const byPrefix = new Map();
  const hiddenIdx = new Set();
  const hiddenIds = new Set();
  const hiddenPrefixes = new Set();
  for (const e of entries || []) {
    if (!e || isIdentityEntry(e)) continue;
    const idx = matchDocumentIndex(e, documents);
    const srv = matchDocumentIndex(e, known);
    if (idx < 0 && srv < 0) continue;       // names a file this export does not have
    const id = idx >= 0 ? String(documents[idx].id ?? documents[idx].hash ?? '') : String(known[srv].id ?? '');
    const prefixes = srv >= 0 && Array.isArray(known[srv].prefixes) ? known[srv].prefixes.map(String) : [];
    if (e.visible === false) {
      if (idx >= 0) hiddenIdx.add(idx);
      if (id) hiddenIds.add(id);
      for (const p of prefixes) hiddenPrefixes.add(p);
      continue;
    }
    const m = buildOverrideMatrix(e, pivot);
    if (idx >= 0) byIndex.set(idx, m);
    if (id) byId.set(id, m);
    for (const p of prefixes) byPrefix.set(p, m);
  }
  // UNCHECKED DOCUMENTS ARE HIDDEN TOO (owner, 2026-10-20: "if linked models
  // are unchecked, they shouldn't show in the 3dviewers fragments or in the
  // project files").
  //
  // There are TWO separate notions of hidden and the viewer only honoured
  // one. A placement entry with visible:false - handled above - is a
  // per-file override. The project's MODEL SOURCE FILTER is the other: the
  // checkboxes on the project card, served as documents[].hidden by
  // GET /projects/:id/model-documents. On SpaceX BA03 sixteen of seventeen
  // files were unchecked and every one of them still drew, because nothing
  // read that flag here.
  //
  // The prefixes come from the SERVER's document list, which is rebuilt from
  // the live version's trees - so an unchecked file whose trees were replaced
  // hides by its new prefixes, not by yesterday's.
  for (let i = 0; i < known.length; i += 1) {
    const d = known[i];
    if (!d || d.hidden !== true) continue;
    const id = String(d.id ?? '');
    if (id) hiddenIds.add(id);
    // matchDocumentIndex speaks the PLACEMENT ENTRY's shape - docId / docName
    // - and a server document calls those id / name. Translating here rather
    // than widening the matcher, which other callers rely on.
    const idx = matchDocumentIndex({ docId: d.id, docName: d.name }, documents);
    if (idx >= 0) hiddenIdx.add(idx);
    for (const p of (Array.isArray(d.prefixes) ? d.prefixes : [])) hiddenPrefixes.add(String(p));
  }

  const matrices = [...new Set([...byIndex.values(), ...byId.values(), ...byPrefix.values()])];
  active = { projectId, byIndex, byId, byPrefix, hiddenIdx, hiddenIds, hiddenPrefixes, matrices, pivot };
  return active;
}

// Called by the streaming loader right after the manifest is parsed.
export async function activate(projectId, manifest) {
  const entries = projectId ? await fetchEntries(projectId) : [];
  if (!entries.length) return activateWith(projectId, manifest, [], []);
  // The server's document list carries each file's element prefixes;
  // fetched only when there is an entry to apply.
  let serverDocuments = [];
  try {
    const r = await getProjectModelDocuments(projectId);
    serverDocuments = Array.isArray(r?.documents) ? r.documents : [];
  } catch (e) { serverDocuments = []; }
  return activateWith(projectId, manifest, entries, serverDocuments);
}

export function deactivate() { active = null; }
export function getActive() { return active; }
export function isActive() {
  return !!(active && (active.matrices.length || active.hiddenIdx.size || active.hiddenIds.size || active.hiddenPrefixes.size));
}

// Forget the cached entries after the dialog saves, so the next viewer
// open sees the new placement.
export function invalidate(projectId) {
  if (projectId) cache.delete(projectId);
  else cache.clear();
}

// The matrix a sidecar row's document adds, or null.
export function transformFor(inst) {
  if (!active || !inst || !active.matrices.length) return null;
  if (inst.doc !== undefined && inst.doc !== null) return active.byId.get(String(inst.doc)) || null;
  if (typeof inst.f === 'number') return active.byIndex.get(inst.f) || null;
  if (active.byPrefix.size && typeof inst.u === 'string' && inst.u.length >= 36) {
    return active.byPrefix.get(inst.u.slice(0, 36)) || null;
  }
  return null;
}

export function isInstanceHiddenByOverride(inst) {
  if (!active || !inst) return false;
  if (inst.doc !== undefined && inst.doc !== null) return active.hiddenIds.has(String(inst.doc));
  if (typeof inst.f === 'number') return active.hiddenIdx.has(inst.f);
  if (active.hiddenPrefixes.size && typeof inst.u === 'string' && inst.u.length >= 36) {
    return active.hiddenPrefixes.has(inst.u.slice(0, 36));
  }
  return false;
}

// A tile's box, grown to hold wherever an override may have moved the
// tile's geometry. Tiles are spatial and shared by documents, so a
// tile is culled by its published box; a file moved a hundred feet
// would otherwise vanish while its tile sat outside the frustum. The
// union over every override is a small over-inclusion, never a miss.
const _tmp = new Box3();
export function expandBox(box) {
  if (!active || !active.matrices.length || !box || box.isEmpty()) return box;
  if (!Number.isFinite(box.min.x) || !Number.isFinite(box.max.x)) return box;
  const out = box.clone();
  for (const m of active.matrices) {
    _tmp.copy(box).applyMatrix4(m);
    out.union(_tmp);
  }
  return out;
}

// Apply the override to one ingested row: returns the row's matrix as
// column-major floats the pools can use directly (cm = true), or null
// when the row's document has no override.
const _m = new Matrix4();
export function applyToRow(inst, deltaMatrix, cm) {
  const d = transformFor(inst);
  if (!d || !deltaMatrix || deltaMatrix.length < 16) return null;
  _m.fromArray(deltaMatrix);
  if (!cm) _m.transpose();          // C# row-major rows -> three's column-vector matrix
  _m.premultiply(d);
  const out = new Float32Array(16);
  _m.toArray(out);
  return out;
}
